import { Alert, Button, Card, Layout, Space, Typography } from 'antd';
import React, { useEffect, useRef } from 'react';

import { BrandLogo } from '@/atoms/BrandLogo';

const { Title, Paragraph } = Typography;

/** Props for the `LoginPage` component. */
export interface LoginPageProps {
  status:
    | 'initializing'
    | 'unauthenticated'
    | 'restoring'
    | 'redirecting'
    | 'processing-callback'
    | 'authenticated'
    | 'error';
  error: string | null;
  onSignIn: () => Promise<void>;
}

/** Full-page Microsoft Entra SSO login screen shown when the user is unauthenticated or the session is being restored. */
export const LoginPage: React.FC<LoginPageProps> = ({ status, error, onSignIn }) => {
  const hasAutoRetried = useRef(false);

  // On first error (stale token, network blip, expired session), clear any remnant
  // state and retry sign-in automatically once before surfacing the error to the user.
  useEffect(() => {
    if (status === 'error' && !hasAutoRetried.current) {
      hasAutoRetried.current = true;
      void onSignIn();
    }
  }, [status, onSignIn]);

  const busy =
    status === 'restoring' || status === 'redirecting' || status === 'processing-callback';

  return (
    <Layout className="login-page">
      <Layout.Content className="login-page__content">
        <div className="login-page__frame">
          <Card variant="borderless" className="login-page__card">
            <Space direction="vertical" size="middle" className="login-page__stack">
              <div className="login-page__brand">
                <BrandLogo colorMode="light" className="login-page__brand-logo" />
              </div>

              <div>
                <Title level={2} className="login-page__title">
                  Welcome back
                </Title>
                <Paragraph className="login-page__subtitle">
                  Sign in to your workspace to continue.
                </Paragraph>
              </div>

              <Button
                type="primary"
                size="large"
                loading={busy}
                disabled={busy}
                onClick={() => {
                  void onSignIn();
                }}
                className="login-page__sign-in-btn"
              >
                Sign in with SSO
              </Button>

              {error ? (
                <Alert type="error" message="Sign-in failed" description={error} showIcon />
              ) : null}
            </Space>
          </Card>
        </div>
      </Layout.Content>
    </Layout>
  );
};

export default LoginPage;
